import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Box,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { userPasswordService } from "../../services/userPasswordService";

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

export const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({
  open,
  onClose,
}) => {
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const toggleShowNewPassword = () => setShowNewPassword(!showNewPassword);
  const toggleShowConfirmPassword = () =>
    setShowConfirmPassword(!showConfirmPassword);

  const isStrongPassword = (password: string): boolean => {
    const regex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return regex.test(password);
  };

  const handlePasswordChange = (password: string) => {
    setNewPassword(password);

    if (password) {
      if (!isStrongPassword(password)) {
        setPasswordError(
          "הסיסמה חייבת לכלול לפחות 8 תווים, אות גדולה, אות קטנה, מספר ותו מיוחד."
        );
      } else {
        setPasswordError(null);
      }
    } else {
      setPasswordError(null);
    }
  };

  const handleSubmit = async () => {
    if (passwordError) return; // מניעת שליחה אם יש שגיאה בסיסמה

    if (newPassword !== confirmPassword) {
      setError("הסיסמאות אינן תואמות. אנא נסה/י שוב.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await userPasswordService.changePassword({ new_password: newPassword });
      setNewPassword("");
      setConfirmPassword("");
      onClose();
    } catch (err) {
      setError("שגיאה במהלך שינוי הסיסמה. אנא נסה/י שוב.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNewPassword("");
    setConfirmPassword("");
    setError(null);
    setPasswordError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" dir="rtl">
      <DialogTitle dir="rtl">שינוי סיסמה</DialogTitle>
      <DialogContent dir="rtl">
        <Box display="flex" alignItems="center" marginBottom={2}>
          <TextField
            label="סיסמה חדשה"
            type={showNewPassword ? "text" : "password"}
            fullWidth
            margin="normal"
            value={newPassword}
            onChange={(e) => handlePasswordChange(e.target.value)} // בדיקת חוזק תוך כדי הקלדה
            InputProps={{
              inputProps: { dir: "ltr" },
            }}
            error={!!passwordError} // הצגת שגיאה אם יש הודעה
            helperText={passwordError} // הצגת הודעת השגיאה
          />
          <IconButton
            onClick={toggleShowNewPassword}
            edge="end"
            sx={{ marginRight: 1 }}
          >
            {showNewPassword ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </Box>
        <Box display="flex" alignItems="center" marginBottom={2}>
          <TextField
            label="אישור סיסמה"
            type={showConfirmPassword ? "text" : "password"}
            fullWidth
            margin="normal"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            InputProps={{
              inputProps: { dir: "ltr" },
            }}
          />
          <IconButton
            onClick={toggleShowConfirmPassword}
            edge="end"
            sx={{ marginRight: 1 }}
          >
            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </Box>
        {error && <p style={{ color: "red" }}>{error}</p>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary" disabled={loading}>
          ביטול
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          disabled={loading || !newPassword || !confirmPassword}
        >
          {loading ? <CircularProgress size={24} /> : "שנה סיסמה"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
